import type { HTMLAttributes } from 'react';
import { cn } from './lib/cn';

export interface BrandSpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  label?: string;
}

export function BrandSpinner({ size = 'md', label, className, ...props }: BrandSpinnerProps) {
  const sizeClasses = {
    sm: 'size-5 border-[2.5px]',
    md: 'size-8 border-3',
    lg: 'size-12 border-4',
    xl: 'size-16 border-[5px]',
  };

  const centerDotSize = {
    sm: 'size-1.5',
    md: 'size-2',
    lg: 'size-3',
    xl: 'size-4',
  };

  return (
    <div className={cn('inline-flex flex-col items-center justify-center gap-3', className)} {...props}>
      <div className="relative grid place-items-center">
        {/* Outer glowing aura */}
        <div
          className={cn(
            'absolute rounded-full bg-[var(--accent)] opacity-35 blur-md animate-pulse',
            sizeClasses[size],
          )}
        />
        {/* Spinning gradient ring */}
        <div
          className={cn(
            'animate-spin rounded-full border-t-[var(--brand)] border-r-[var(--accent)] border-b-[var(--surface-raised)] border-l-transparent shadow-sm',
            sizeClasses[size],
          )}
        />
        {/* Inner brand pulsing core */}
        <div
          className={cn(
            'absolute rounded-full bg-[var(--accent)] animate-pulse shadow-[0_0_8px_var(--accent)]',
            centerDotSize[size],
          )}
        />
      </div>
      {label && (
        <span className="text-xs font-extrabold tracking-wide text-[var(--muted-foreground)] animate-pulse">
          {label}
        </span>
      )}
    </div>
  );
}
